import { useState, type CSSProperties } from "react";
import { Button, Title } from "lola-framework-ui-test";
import { useTranslation } from "react-i18next";
import { NotificationBellIcon } from "../../assets/icons";
import { TITLE_COLOR } from "../../constants";
import { useStore } from "../../store/globalState";
import { Popup } from "../Popup";
import { ScrollFadeIndicator } from "../ScrollFadeIndicator";
import { CloseLight } from "../../assets/navIcons";

/** Newest first — same order as the live widget inbox after list refresh. */
export const GALLERY_NOTICE_FIXTURES = [
  {
    id: "ntf_gallery_1",
    titleKey: "notifications.exampleSubject",
    bodyKey: "notifications.exampleBody",
    read: false,
    createdAt: "2026-10-22T13:05:00Z",
  },
  {
    id: "ntf_gallery_2",
    titleKey: "notifications.exampleSubject2",
    bodyKey: "notifications.exampleBody2",
    read: false,
    createdAt: "2026-10-21T18:38:00Z",
  },
  {
    id: "ntf_gallery_3",
    titleKey: "notifications.exampleSubject3",
    bodyKey: "notifications.exampleBody3",
    read: true,
    createdAt: "2026-10-19T09:12:00Z",
  },
  {
    id: "ntf_gallery_4",
    titleKey: "notifications.exampleSubject4",
    bodyKey: "notifications.exampleBody4",
    read: true,
    createdAt: "2026-10-14T16:47:00Z",
  },
  {
    id: "ntf_gallery_5",
    titleKey: "notifications.exampleSubject5",
    bodyKey: "notifications.exampleBody5",
    read: true,
    createdAt: "2026-10-03T11:20:00Z",
  },
  {
    id: "ntf_gallery_6",
    titleKey: "notifications.exampleSubject6",
    bodyKey: "notifications.exampleBody6",
    read: true,
    createdAt: "2026-09-27T20:02:00Z",
  },
] as const;

export interface GalleryInboxItem {
  id: string;
  title: string;
  body: string;
  isRead: boolean;
  /** ISO timestamp. The drawer shows it only when present. */
  createdAt?: string;
}

function formatNoticeDate(iso: string, language: string): string {
  return new Intl.DateTimeFormat(language, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(iso));
}

interface ChromeProps {
  ns: "fund-request" | "payout-claim" | "send" | "request" | "deferred-origination" | "m2m";
  unread: number;
  /** Theme title slot. A gradient is reduced to its first hex for the SVG fill. */
  bellColor: string;
  badgeBackground: string;
  badgeTextColor: string;
  onOpen: () => void;
}

/** SVG `fill` cannot paint a CSS gradient. */
function solidFill(value: string): string {
  const hex = value.match(/#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})\b/);
  return hex?.[0] ?? value;
}

/** Bell for the transactions header. No Novu. */
export function GalleryInboxChrome({
  ns,
  unread,
  bellColor,
  badgeBackground,
  badgeTextColor,
  onOpen,
}: ChromeProps) {
  const { t } = useTranslation(ns);

  return (
    <button
      type="button"
      className="notifications-bell"
      aria-label={t("notifications.bellLabel")}
      onClick={(event) => {
        event.stopPropagation();
        onOpen();
      }}
    >
      <NotificationBellIcon size={24} color={solidFill(bellColor)} />
      {unread > 0 ? (
        <span
          className="notifications-bell-badge"
          style={{ background: badgeBackground, color: badgeTextColor }}
        >
          {unread}
        </span>
      ) : null}
    </button>
  );
}

type GalleryNs = "fund-request" | "payout-claim" | "send" | "request" | "deferred-origination" | "m2m";

interface DrawerProps {
  ns: GalleryNs;
  items: GalleryInboxItem[];
  open: boolean;
  onClose: () => void;
  onMarkRead: (id: string) => void;
  onArchive: (id: string) => void;
  onOpenPreferences: () => void;
}

/** Same bottom sheet anatomy as the payout-method and support sheets. */
export function GalleryInboxDrawer({
  ns,
  items,
  open,
  onClose,
  onMarkRead,
  onArchive,
  onOpenPreferences,
}: DrawerProps) {
  const { t, i18n } = useTranslation(ns);
  const { backgroundBtn, textColorBtn, bodyCopy } = useStore().theme.whiteView;
  const actionColor = solidFill(backgroundBtn);

  return (
    <Popup visible={open} onClose={onClose}>
      <section
        className="notifications-sheet"
        role="dialog"
        aria-label={t("notifications.title")}
      >
        <header className="payout-details-header">
          <span className="payout-header-action" aria-hidden="true" />
          <Title
            title={t("notifications.title")}
            align="center"
            color={bodyCopy}
          />
          <button
            type="button"
            className="payout-header-action payout-close-btn"
            aria-label={t("notifications.close")}
            onClick={onClose}
          >
            <CloseLight size={36} colors={[bodyCopy, bodyCopy]} />
          </button>
        </header>
        {items.length === 0 ? (
          <p className="notifications-sheet-empty" style={{ color: bodyCopy }}>
            {t("notifications.empty")}
          </p>
        ) : (
          <ul className="notifications-sheet-list">
            <ScrollFadeIndicator color={bodyCopy} />
            {items.map((item) => (
              <li key={item.id} data-read={item.isRead}>
                <span className="notifications-sheet-title">
                  {!item.isRead ? (
                    <span
                      className="notifications-sheet-dot"
                      style={{ background: backgroundBtn }}
                      aria-hidden="true"
                    />
                  ) : null}
                  <strong style={{ color: TITLE_COLOR }}>{item.title}</strong>
                </span>
                {item.body ? (
                  <p style={{ color: bodyCopy }}>{item.body}</p>
                ) : null}
                {item.createdAt ? (
                  <time
                    className="notifications-sheet-date"
                    dateTime={item.createdAt}
                    style={{ color: bodyCopy }}
                  >
                    {formatNoticeDate(item.createdAt, i18n.language)}
                  </time>
                ) : null}
                <div className="notifications-sheet-actions">
                  {!item.isRead ? (
                    <button
                      type="button"
                      style={{ color: actionColor }}
                      onClick={() => onMarkRead(item.id)}
                    >
                      {t("notifications.markRead")}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    style={{ color: actionColor }}
                    onClick={() => onArchive(item.id)}
                  >
                    {t("notifications.archive")}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <p className="notifications-sheet-foot" style={{ color: bodyCopy }}>
          {t("notifications.retention")}
        </p>
        <Button
          size={ns === "m2m" ? "small" : "large"}
          background={backgroundBtn}
          color={textColorBtn}
          className="notifications-pref-button"
          onClick={onOpenPreferences}
        >
          {t("notifications.preferences")}
        </Button>
      </section>
    </Popup>
  );
}

const PREFERENCE_CHANNELS = ["in_app", "email", "sms"] as const;

const CHANNEL_LABEL = {
  in_app: "notifications.channelInApp",
  email: "notifications.channelEmail",
  sms: "notifications.channelSms",
} as const;

export function GalleryPreferencesSheet({
  ns,
  open,
  onClose,
}: {
  ns: GalleryNs;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation(ns);
  const { backgroundBtn, bodyCopy } = useStore().theme.whiteView;
  const [enabled, setEnabled] = useState<Record<string, boolean>>({});
  if (!open) return null;

  return (
    <Popup visible={open} onClose={onClose}>
      <section
        className="notifications-sheet"
        role="dialog"
        aria-label={t("notifications.preferencesTitle")}
      >
        <header className="payout-details-header">
          <span className="payout-header-action" aria-hidden="true" />
          <Title
            title={t("notifications.preferencesTitle")}
            align="center"
            color={bodyCopy}
          />
          <button
            type="button"
            className="payout-header-action payout-close-btn"
            aria-label={t("notifications.close")}
            onClick={onClose}
          >
            <CloseLight size={36} colors={[bodyCopy, bodyCopy]} />
          </button>
        </header>
        <ul className="notifications-pref-list">
          {PREFERENCE_CHANNELS.map((channel) => {
            const on = enabled[channel] === true;
            const label = t(CHANNEL_LABEL[channel]);
            return (
              <li key={channel}>
                <span style={{ color: bodyCopy }}>{label}</span>
                <button
                  type="button"
                  role="switch"
                  className="notifications-pref-switch"
                  aria-checked={on}
                  aria-label={label}
                  style={{ "--bg": backgroundBtn } as CSSProperties}
                  onClick={() =>
                    setEnabled((current) => ({ ...current, [channel]: !on }))
                  }
                >
                  <span />
                </button>
              </li>
            );
          })}
        </ul>
        <p className="notifications-sheet-foot" style={{ color: bodyCopy }}>
          {t("notifications.preferencesEssential")}
        </p>
      </section>
    </Popup>
  );
}
